import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useCapabilities } from "@/apps/useCapabilities";
import { usePermissions } from "@/core/auth";
import { focusedGroupIdAtom, layoutAtom } from "@/core/state";
import { findNodeById, listAllPanelInstances } from "@/shell/layout";
import { panelRegistry } from "@/shell/PanelRegistry";
import { getPanelVisual } from "@/shell/PanelThumbnails";
import { Button, Infobulle, cn } from "@/shared/ui";

import { ECRANS_DU_BUREAU, ecranDuRail, useOuvrirEcran } from "./ecransDuBureau";
import { isPanelVisible } from "./panelVisibility";

/**
 * UNE icône du rail (le Bureau et le kiosque la partagent) : un `Button` fantôme de 36 px, son nom
 * en `Infobulle` à droite. L'écran ACTIF est un bouton de verre (`secondary`) précédé du trait de
 * la marque — la marque sur ce qui est CHOISI ; un écran simplement OUVERT a son icône pleine et
 * son encre pleine.
 */
export function ItemDuRail({
  titre,
  Icone,
  actif = false,
  ouvert = false,
  onClick,
}: {
  titre: string;
  Icone: ReturnType<typeof getPanelVisual>["icon"];
  actif?: boolean;
  ouvert?: boolean;
  onClick: () => void;
}) {
  return (
    <div className="relative">
      {actif && <span aria-hidden className="og-trait absolute -left-1.5 bottom-2.5 top-2.5 w-0.5" />}
      <Infobulle texte={titre} cote="droite">
        <Button
          variant={actif ? "secondary" : "ghost"}
          size="lg"
          iconOnly
          className={cn("h-9 w-9", !actif && ouvert && "text-fg")}
          aria-label={titre}
          aria-current={actif ? "page" : undefined}
          onClick={onClick}
        >
          <Icone className="h-[18px] w-[18px]" weight={actif || ouvert ? "fill" : "regular"} />
        </Button>
      </Infobulle>
    </div>
  );
}

/**
 * Les écrans du Bureau, une icône chacun, en colonne dans le rail gauche —
 * le même dessin que `EcransDuKiosque` (le rail des chatteurs, que Kayla a
 * montré comme modèle le 08-10-2026 : « tu me fais la même chose, juste que
 * moi, j'ai un peu plus de choses »). Un clic ouvre l'écran ou le remet devant.
 *
 * Deux états lisibles : OUVERT (une instance vit dans le layout : icône
 * pleine, texte plein) et ACTIF (c'est l'onglet actif du groupe focalisé :
 * fond relevé + trait de marque). Rien d'autre — ni épingle, ni menu, ni
 * glisser-déposer, ni barre de tâches.
 */
export function EcransDuBureau() {
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  const { capabilities } = useCapabilities();
  const layout = useAtomValue(layoutAtom);
  const focaliseId = useAtomValue(focusedGroupIdAtom);
  const ouvrir = useOuvrirEcran();

  const ecrans = useMemo(() => {
    if (!permissions || !capabilities) return [];
    return ECRANS_DU_BUREAU.filter((id) =>
      isPanelVisible(panelRegistry[id], {
        permissions,
        capabilities,
      }),
    );
  }, [permissions, capabilities]);

  const ouverts = useMemo(
    () => new Set(listAllPanelInstances(layout).map((i) => i.panelId)),
    [layout],
  );
  const focalise = focaliseId ? findNodeById(layout, focaliseId) : null;
  const actif =
    focalise && focalise.kind === "tabs"
      ? focalise.panels[focalise.activeIndex]?.panelId
      : undefined;

  return (
    <div className="flex flex-col items-center gap-1">
      {ecrans.map((id) => {
        const titre = t(`panels.registry.${id}.title`);
        return (
          <ItemDuRail
            key={id}
            titre={titre}
            Icone={getPanelVisual(id).icon}
            actif={ecranDuRail(actif) === id}
            ouvert={ouverts.has(id)}
            onClick={() => ouvrir(id)}
          />
        );
      })}
    </div>
  );
}
